import { Add } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import AccordionSection from '../../components/AccordionSection';
import AppLink from '../../components/common/AppLink/AppLink';
import AddTaskTemplateModal from '../../components/common/TaskTemplatesSection/AddTaskTemplateModal';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../routes/Routes';
import { listPageTableRowStyling } from '../../styles/sxStyling';
import { TaskTemplateLink } from '../../types/TaskTemplate';
import { UserSettingsCollapseKeys } from '../../types/User';
import ResponsiveButton from '../../uikit/buttons/ResponsiveButton';
import { renderTaskStartCondition } from '../../utility/task';

type TaskTemplateSectionsProps = {
  linkingModels: TaskTemplateLink[];
  setLinkingModels: (templates: TaskTemplateLink[]) => void;
};

export default function TaskTemplateSections({ linkingModels, setLinkingModels }: TaskTemplateSectionsProps) {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedTaskTemplateLink, setSelectedTaskTemplateLink] = useState<TaskTemplateLink | null>(null);
  const { disabled } = useContext(DisableContext);

  const sortedLinkingModels = _.sortBy(linkingModels, 'due_date_offset');

  const handleAddOrUpdateTaskTemplate = (linkingData: TaskTemplateLink) => {
    const updatedModels = _.includes(_.map(linkingModels, 'uuid'), linkingData.uuid)
      ? _.map(linkingModels, (model) => (model.uuid === linkingData.uuid ? linkingData : model))
      : [...linkingModels, linkingData];

    setLinkingModels(updatedModels);
    setModalOpen(false);
    setSelectedTaskTemplateLink(null);
  };

  const handleOpenTaskTemplateModal = (linkingModel: TaskTemplateLink | null) => {
    setSelectedTaskTemplateLink(linkingModel);
    setModalOpen(true);
  };

  const handleRemoveTaskTemplate = (uuid: string) => {
    const updatedModels = _.reject(linkingModels, { uuid });
    setLinkingModels(updatedModels);
  };

  const taskTableActionButtons = (
    <Box sx={{ display: 'inline' }}>
      <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => handleOpenTaskTemplateModal(null)} disabled={disabled}>
        Add
      </ResponsiveButton>
    </Box>
  );

  const taskTableRows = _.map(sortedLinkingModels, (linkingModel: TaskTemplateLink) => (
    <TableRow key={linkingModel.uuid} sx={{ ...listPageTableRowStyling }}>
      <TableCell>
        <AppLink to={`/${ROUTE_PATHS.TASK_TEMPLATES}/${linkingModel.task_template_uuid}`}>{linkingModel.task_template_name}</AppLink>
      </TableCell>
      <TableCell>
        {renderTaskStartCondition(
          linkingModel,
          _.map(sortedLinkingModels, (m) => ({
            uuid: m.uuid,
            name: m.task_template_name,
          }))
        )}
      </TableCell>
      <TableCell>{linkingModel.due_date_offset}</TableCell>
      <TableCell>
        <AppLink to={`/${ROUTE_PATHS.EMPLOYEES}/${linkingModel.assigned_to_uuid}`}>{linkingModel.assigned_to_display_name}</AppLink>
      </TableCell>
      <TableCell>{linkingModel.task_type_value}</TableCell>
      <TableCell>
        {!disabled && (
          <>
            <IconButton onClick={() => handleOpenTaskTemplateModal(linkingModel)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => handleRemoveTaskTemplate(linkingModel.uuid)}>
              <DeleteIcon />
            </IconButton>
          </>
        )}
      </TableCell>
    </TableRow>
  ));

  return (
    <>
      <AccordionSection title="Task Templates" headerElements={taskTableActionButtons} collapseId={UserSettingsCollapseKeys.EVENT_TEMPLATE_TASK_TEMPLATES}>
        <Table sx={{ mx: 1, mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Start Condition</TableCell>
              <TableCell>Due Date Offset</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Task Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{taskTableRows}</TableBody>
        </Table>
      </AccordionSection>

      <AddTaskTemplateModal
        isOpen={isModalOpen}
        onCancel={() => setModalOpen(false)}
        onAdd={handleAddOrUpdateTaskTemplate}
        taskTemplateLink={selectedTaskTemplateLink}
        dependencyOptions={_.map(sortedLinkingModels, (model) => ({
          uuid: model.uuid,
          name: model.task_template_name,
        }))}
        disabledConditions={['date']}
      />
    </>
  );
}
